import { X } from "lucide-react";
import {
  groupedToolsForEnv,
  type ToolDef,
  type ToolId,
} from "../lib/readerTools";
import { TOOL_ICONS } from "../lib/toolVisuals";

const HOME_TOOL_ORDER: ToolId[] = [
  "compare",
  "word-to-pdf",
  "pdf-split",
  "pdf-merge",
  "to-image",
  "scan-document",
];

type Props = {
  env: "docx" | "pdf" | "home";
  onSelect: (id: ToolId) => void;
  onClose: () => void;
};

function ToolGridButton({
  tool,
  variant,
  onSelect,
}: {
  tool: ToolDef;
  variant: "home" | "modal";
  onSelect: (id: ToolId) => void;
}) {
  const Icon = TOOL_ICONS[tool.id];
  if (variant === "home") {
    return (
      <button
        type="button"
        className="home-tool-card home-tool-card-polished"
        onClick={() => onSelect(tool.id)}
      >
        <span className={`home-tool-visual home-tool-visual-${tool.id}`} aria-hidden>
          <Icon size={18} />
          <span className="home-tool-visual-bar" />
          <span className="home-tool-visual-bar short" />
        </span>
        <span className="home-tool-copy">
          <strong>{tool.label}</strong>
          <span>{tool.blurb}</span>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className="tools-grid-item"
      onClick={() => onSelect(tool.id)}
    >
      <span className="tool-icon" aria-hidden>
        <Icon size={18} />
      </span>
      {tool.label}
    </button>
  );
}

export function ToolsMenu({ env, onSelect, onClose }: Props) {
  const groups = groupedToolsForEnv(env);
  return (
    <div
      className="tools-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="tools-modal"
        role="dialog"
        aria-label="ابزارها"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tools-modal-header">
          <span>ابزارها</span>
          <button
            type="button"
            className="icon-btn"
            aria-label="بستن"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
        <div className="tools-modal-body">
          {groups.map((section) => (
            <section
              key={section.group}
              className="tools-grid-group"
              aria-label={section.label}
            >
              <h3>{section.label}</h3>
              <div className="tools-grid">
                {section.tools.map((t) => (
                  <ToolGridButton
                    key={t.id}
                    tool={t}
                    variant="modal"
                    onSelect={onSelect}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

type HomeProps = {
  onSelect: (id: ToolId) => void;
};

export function HomeToolsGrid({ onSelect }: HomeProps) {
  const groups = groupedToolsForEnv("home");
  const tools = HOME_TOOL_ORDER.map((id) =>
    groups.flatMap((section) => section.tools).find((tool) => tool.id === id),
  ).filter((tool): tool is ToolDef => Boolean(tool));

  return (
    <div className="home-tools home-tools-curated">
      <div className="home-tools-grid curated">
        {tools.map((t) => (
          <ToolGridButton
            key={t.id}
            tool={t}
            variant="home"
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
